import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/api/client';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Building2, Plus, Edit2, Trash2, MapPin } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import { extractPaginatedData } from '@/utils/pagination';

// Branch API
const branchesAPI = {
  // per_page is passed explicitly so this page keeps its paginated table, while
  // dropdown callers elsewhere get the full branch list.
  list: ({ page = 1, per_page = 20 } = {}) => api.get('/branches', { params: { page, per_page } }),
  create: (data: any) => api.post('/branches', data),
  update: (id: number, data: any) => api.patch(`/branches/${id}`, data),
  delete: (id: number) => api.delete(`/branches/${id}`),
};

export default function BranchesPage() {
  const perPage = 6;
  const { can, hasRole } = useAuth();
  const canManage = can('manage_branches');
  const canSelectOrganization = hasRole('super_admin');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingBranch, setEditingBranch] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: branchesData, isLoading } = useQuery({
    queryKey: ['branches', page],
    // This page paginates its own table, so it opts into pagination explicitly.
    queryFn: () => branchesAPI.list({ page, per_page: 20 }),
  });

  const { data: organizationsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.get('/organizations'),
    enabled: canSelectOrganization,
  });

  const organizations = Array.isArray(organizationsData?.data)
    ? organizationsData.data
    : organizationsData?.data?.data || [];

  const createMutation = useMutation({
    mutationFn: (data: any) => branchesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      setShowForm(false);
      toast.success('Филиал успешно создан');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => branchesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      setEditingBranch(null);
      toast.success('Филиал успешно обновлен');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => branchesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Филиал удален');
    },
  });

  const { items: branches, meta } = extractPaginatedData<any>(branchesData, page, perPage);

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить филиал?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Филиалы</h1>
        {canManage && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить филиал
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm || !!editingBranch}
        onClose={() => {
          setShowForm(false);
          setEditingBranch(null);
        }}
        title={editingBranch ? 'Редактировать филиал' : 'Новый филиал'}
      >
        <BranchForm
          initialData={editingBranch}
          organizations={organizations}
          canSelectOrganization={canSelectOrganization}
          onSubmit={async (data) => {
            if (editingBranch) {
              await updateMutation.mutateAsync({ id: editingBranch.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingBranch(null);
          }}
        />
      </Modal>

      {/* Branches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((branch: any) => (
          <Card key={branch.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Building2 className="w-8 h-8 text-blue-600" />
                  <div>
                    <h3 className="font-semibold">{branch.name}</h3>
                    <p className="text-sm text-gray-500">{branch.code}</p>
                  </div>
                </div>
                <Badge variant={branch.is_active ? 'success' : 'secondary'}>
                  {branch.is_active ? 'Активен' : 'Неактивен'}
                </Badge>
              </div>

              <div className="space-y-2 text-sm mb-4">
                {branch.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{branch.address}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Сотрудники:</span>
                  <span>{branch.employees_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Устройства:</span>
                  <span>{branch.devices_count || 0}</span>
                </div>
              </div>

              {canManage && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingBranch(branch)}
                    className="flex-1"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Редактировать
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(branch.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Pagination meta={meta} onPageChange={setPage} />

      {branches.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Нет филиалов</h3>
          <p className="text-gray-500">Создайте филиал для начала работы.</p>
        </div>
      )}
    </div>
  );
}

function BranchForm({
  initialData,
  organizations,
  canSelectOrganization,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  organizations: any[];
  canSelectOrganization: boolean;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    code: initialData?.code || '',
    address: initialData?.address || '',
    phone: initialData?.phone || '',
    timezone: initialData?.timezone || 'Asia/Tashkent',
    org_id: initialData?.org_id || '',
    is_active: initialData?.is_active ?? true,
  });

  return (
    <div className="space-y-4">
      {canSelectOrganization && organizations.length > 0 && (
        <select
          className="w-full px-4 py-2 border rounded-lg"
          value={formData.org_id}
          onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
        >
          <option value="">Выберите организацию</option>
          {organizations.map((org: any) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          placeholder="Название филиала"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <Input
          placeholder="Код (напр., HQ, BRANCH1)"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        />
      </div>
      <Input
        placeholder="Адрес"
        value={formData.address}
        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          placeholder="Телефон"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        />
        <Input
          placeholder="Часовой пояс"
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={() => onSubmit(formData)} className="flex-1">
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}


